import { useState } from 'react';
import {
  useMyExternalTrainings,
  useDeleteExternalTraining,
  type ExternalTraining,
} from '@/api/external-trainings';
import { PositionFilter } from '@/components/shared/position-filter';
import { LoadingSpinner } from '@/components/shared/loading-spinner';
import { ErrorMessage } from '@/components/shared/error-message';
import { EmptyState } from '@/components/shared/empty-state';
import { Pagination } from '@/components/shared/pagination';
import { formatDate } from '@/lib/formatters';
import {
  BookPlus,
  Plus,
  FileText,
  Building2,
  Calendar,
  Trash2,
  AlertTriangle,
  X,
} from 'lucide-react';
import { AddTrainingDialog } from './add-dialog';

export function ExternalTrainingsPage() {
  const [page, setPage] = useState(1);
  const [showDialog, setShowDialog] = useState(false);
  const [positionId, setPositionId] = useState<number | undefined>(undefined);

  const { data: res, isLoading, error } = useMyExternalTrainings(page, positionId);

  const trainings = res?.data ?? [];
  const pagination = res?.meta?.pagination;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Capacitaciones Externas</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Registra capacitaciones realizadas fuera de la plataforma.
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowDialog(true)}
          className="inline-flex items-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90"
        >
          <Plus className="h-4 w-4" />
          Agregar
        </button>
      </div>

      <PositionFilter value={positionId} onChange={setPositionId} />

      {/* Content */}
      {isLoading ? (
        <LoadingSpinner />
      ) : error ? (
        <ErrorMessage message="No se pudieron cargar las capacitaciones externas." />
      ) : trainings.length === 0 ? (
        <EmptyState
          title="Sin capacitaciones externas"
          description="No has registrado capacitaciones externas aun."
        />
      ) : (
        <>
          <div className="space-y-3">
            {trainings.map((t) => (
              <TrainingCard key={t.id} training={t} />
            ))}
          </div>
          {pagination && <Pagination meta={pagination} onPageChange={setPage} />}
        </>
      )}

      {/* Add dialog */}
      {showDialog && <AddTrainingDialog onClose={() => setShowDialog(false)} />}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Training Card                                                     */
/* ------------------------------------------------------------------ */

function TrainingCard({ training }: { training: ExternalTraining }) {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const deleteMutation = useDeleteExternalTraining();

  return (
    <>
      <div className="rounded-xl border border-border bg-card p-5 transition-shadow hover:shadow-md">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-600">
            <BookPlus className="h-5 w-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-2">
              <h3 className="text-sm font-semibold text-card-foreground">{training.title}</h3>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="shrink-0 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-destructive/10 hover:text-destructive"
                title="Eliminar"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            </div>

            {training.institution && (
              <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                <Building2 className="h-3.5 w-3.5" />
                <span>{training.institution}</span>
              </div>
            )}

            {training.description && (
              <p className="mt-2 line-clamp-2 text-xs text-muted-foreground">
                {training.description}
              </p>
            )}

            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-muted-foreground">
              {training.completed_at && (
                <span className="flex items-center gap-1">
                  <Calendar className="h-3.5 w-3.5" />
                  Completada: {formatDate(training.completed_at)}
                </span>
              )}
              <span>Registrada: {formatDate(training.created_at)}</span>
            </div>

            {training.file_url && (
              <a
                href={training.file_url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-3 inline-flex items-center gap-1.5 rounded-lg border border-border px-3 py-1.5 text-xs font-medium text-card-foreground transition-colors hover:bg-accent"
              >
                <FileText className="h-3.5 w-3.5" />
                {training.file_name ?? 'Ver archivo'}
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Delete confirmation modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="mx-4 w-full max-w-sm rounded-xl border border-border bg-card p-6 shadow-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-destructive" />
                <h3 className="text-sm font-semibold text-card-foreground">
                  Confirmar eliminación
                </h3>
              </div>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4" />
              </button>
            </div>
            <p className="mt-3 text-sm text-muted-foreground">
              ¿Está seguro de que desea eliminar la capacitación externa{' '}
              <span className="font-medium text-card-foreground">
                &ldquo;{training.title}&rdquo;
              </span>
              ? Esta acción no se puede deshacer.
            </p>
            {deleteMutation.error && (
              <p className="mt-2 text-xs text-destructive">{deleteMutation.error.message}</p>
            )}
            <div className="mt-4 flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="rounded-lg border border-border px-4 py-2 text-sm font-medium text-card-foreground transition-colors hover:bg-accent"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => {
                  deleteMutation.mutate(training.id, {
                    onSuccess: () => setShowDeleteConfirm(false),
                  });
                }}
                disabled={deleteMutation.isPending}
                className="rounded-lg bg-destructive px-4 py-2 text-sm font-medium text-destructive-foreground transition-colors hover:bg-destructive/90 disabled:opacity-50"
              >
                {deleteMutation.isPending ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
